import api from './api'
import {
  BotState,
  GridConfig,
  RiskConfig,
  GridLevel,
  Position,
  Order,
  MarketPrice,
  OHLCVData,
  BacktestParams,
  BacktestResult,
  OptimizationParams,
  BotStats,
  HealthCheck
} from '@/types'

export const botService = {
  // Control del bot
  async startBot(): Promise<{ status: string; mensaje: string; hora_inicio: string }> {
    const response = await api.post('/bot/iniciar')
    return response.data
  },

  async stopBot(): Promise<{ status: string; mensaje: string; duracion_ejecucion?: string }> {
    const response = await api.post('/bot/detener')
    return response.data
  },

  async getBotState(): Promise<BotState> {
    const response = await api.get<BotState>('/bot/estado')
    return response.data
  },

  // Alias for monitoring page
  async getBotStatus(): Promise<BotState> {
    return this.getBotState()
  },

  async executeCycle(): Promise<{ status: string; timestamp: string; resultado: any }> {
    const response = await api.post('/bot/ciclo')
    return response.data
  },

  // Configuración
  async getGridConfig(): Promise<GridConfig> {
    const response = await api.get<GridConfig>('/configuracion/grid')
    return response.data
  },

  async updateGridConfig(config: GridConfig): Promise<{ status: string; mensaje: string }> {
    const response = await api.put('/configuracion/grid', config)
    return response.data
  },

  async getRiskConfig(): Promise<RiskConfig> {
    const response = await api.get<RiskConfig>('/configuracion/riesgo')
    return response.data
  },

  async updateRiskConfig(config: RiskConfig): Promise<{ status: string; mensaje: string }> {
    const response = await api.put('/configuracion/riesgo', config)
    return response.data
  },

  async updateMarketConfig(config: any): Promise<{ status: string; mensaje: string }> {
    const response = await api.put('/configuracion/mercado', config)
    return response.data
  },

  async getMarketConfig(): Promise<any> {
    const response = await api.get('/configuracion/mercado')
    return response.data
  },

  async getCompleteConfig(): Promise<any> {
    const response = await api.get('/configuracion/completa')
    return response.data
  },

  // Grid Trading
  async getGridLevels(): Promise<{ total_niveles: number; niveles: GridLevel[]; precio_actual?: number; fuera_de_rango?: boolean; rango_grid?: { min: number; max: number } }> {
    const response = await api.get('/grid/niveles')
    return response.data
  },

  async reinitializeGrid(): Promise<{ status: string; mensaje: string; niveles_creados: number }> {
    const response = await api.post('/grid/reinicializar')
    return response.data
  },

  async getPositions(): Promise<{ total_posiciones: number; posiciones: Position[] }> {
    const response = await api.get('/grid/posiciones')
    return response.data
  },

  async getOrders(): Promise<{ total_ordenes: number; ordenes: Order[] }> {
    const response = await api.get('/grid/ordenes')
    return response.data
  },

  // Alias for monitoring page
  async getGridOrders(): Promise<Order[]> {
    const response = await this.getOrders()
    return response.ordenes
  },

  // Mercado
  async getCurrentPrice(): Promise<MarketPrice> {
    const response = await api.get<MarketPrice>('/mercado/precio')
    return response.data
  },

  async getPrice24h(): Promise<{
    par: string;
    datos: Array<{ timestamp: string; precio: number }>;
  }> {
    try {
      const response = await api.get('/mercado/precio-24h')
      return response.data
    } catch (error) {
      console.error('Error en getPrice24h:', error)
      throw error
    }
  },

  async getOHLCVData(limit: number = 100, timeframe: string = '15m'): Promise<{
    par: string;
    timeframe: string;
    registros: number;
    datos: OHLCVData[];
  }> {
    const response = await api.get('/mercado/ohlcv', {
      params: { limite: limit, timeframe }
    })
    return response.data
  },

  async getBalance(): Promise<any> {
    const response = await api.get('/mercado/balance')
    return response.data
  },

  // Backtesting
  async runBacktest(params: BacktestParams): Promise<{
    status: string;
    periodo: { inicio: string; fin: string };
    metricas: BacktestResult;
    capital: { inicial: number; final: number };
  }> {
    const response = await api.post('/backtest/ejecutar', params)
    return response.data
  },

  async optimizeParameters(params: OptimizationParams): Promise<{
    status: string;
    mejores_parametros: any;
    total_combinaciones_probadas: number;
    top_5_combinaciones: any[];
  }> {
    const response = await api.post('/backtest/optimizar', params)
    return response.data
  },

  // Órdenes manuales
  async createManualOrder(order: {
    tipo: 'limit' | 'market';
    lado: 'buy' | 'sell';
    cantidad: number;
    precio?: number;
  }): Promise<any> {
    const response = await api.post('/ordenes/crear', order)
    return response.data
  },

  async getOrderHistory(): Promise<{ total_ordenes: number; ordenes: any[] }> {
    const response = await api.get('/ordenes/historial')
    return response.data
  },

  // Alias for monitoring page
  async getTradeHistory(): Promise<any[]> {
    const response = await this.getOrderHistory()
    return response.ordenes
  },

  // Estadísticas
  async getStats(): Promise<BotStats> {
    const response = await api.get<BotStats>('/estadisticas/resumen')
    return response.data
  },

  // Health check
  async getHealth(): Promise<HealthCheck> {
    const response = await api.get<HealthCheck>('/salud')
    return response.data
  },

  // Exchange Credentials Management
  async getExchangeCredentials(): Promise<any[]> {
    const response = await api.get('/exchanges/credentials')
    return response.data
  },

  async saveExchangeCredentials(credentials: any): Promise<{ status: string; message: string }> {
    const response = await api.post('/exchanges/credentials', credentials)
    return response.data
  },

  async deleteExchangeCredentials(exchangeId: string): Promise<{ status: string; message: string }> {
    const response = await api.delete(`/exchanges/credentials/${exchangeId}`)
    return response.data
  },

  async testExchangeConnection(exchangeId: string): Promise<any> {
    const response = await api.post('/exchanges/test-connection', exchangeId)
    return response.data
  },

  async getSupportedExchanges(): Promise<{ supported_exchanges: any[] }> {
    const response = await api.get('/exchanges/supported')
    return response.data
  }
}